'use strict';

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { pluralize, singularize } from 'inflected';
import { useGroceryDisplay } from './hooks';
import useGroceryMealUsage from './hooks/useGroceryMealUsage';
import { useMealPlanData } from '../../contexts/MealPlanContext';
import SmartFraction from '@components/Widgets/SmartFraction.react';
import ImgResized from '@components/Widgets/ImgResized.react';
import Popup from '@components/Widgets/Popup.react';
import { smartCeil, humanizeGrams, humanizeMilliliters, inflectUnitOfMeasure, roundForHumans } from '@utils/Math';
import { flattenIngredients } from '@utils/Recipe';
import { isProSide } from '@utils/Env';
import UserStore from '@stores/UserStore';
import './GroceriesModal.scss';
import '@pro/components/Modals/Modals.scss';

/**
 * Empty state component that provides helpful guidance when no groceries are available
 */
const EmptyGroceryState = () => {
    return (
        <div className="grocery-empty-state">
            <div className="empty-state-icon">
                <i className="icon-shopping-cart" />
            </div>
            <div className="empty-state-content">
                <h3>Your grocery list is empty</h3>
                <div className="empty-state-suggestions">
                    <p>Try adding some meals to your plan to see their groceries here</p>
                </div>
                <div className="empty-state-tip">
                    <i className="icon-lightbulb" />
                    <span>Tip: Each recipe you add will automatically calculate the ingredients you need to buy</span>
                </div>
            </div>
        </div>
    );
};

const GroceriesModal = ({ closeModal, profile }) => {
    const mealPlanData = useMealPlanData();
    const groceries = mealPlanData?.groceries || [];
    const foods = mealPlanData?.foods || {};
    const recipes = mealPlanData?.recipes || {};
    const details = mealPlanData?.details || {};
    const user = mealPlanData?.user || {};

    const [showExtended, setShowExtended] = useState(false);

    // Check if user has access to admin/detailed features
    const isAdminOrDnt = UserStore.getDnt() || user?.role === 'admin';

    // Use our custom hooks
    const { categorized, totals, freshTotals, pantryTotals } = useGroceryDisplay(groceries);
    const { mealUsageData } = useGroceryMealUsage(groceries);

    const handleToggleExtended = useCallback(() => {
        setShowExtended(prev => !prev);
    }, []);

    // Rendering functions ported from old version
    const renderByDisplayMode = (grocery) => {
        const { preferences = {} } = profile || {};
        let icon = [];
        let className = "grocery";
        let food;

        // For weights, we need to pluralize or suffixize the FIRST word (not the last)
        let avoidances = [];

        if (grocery.food_uuid && foods && foods[grocery.food_uuid] && preferences.avoidances) {
            food = foods[grocery.food_uuid];
            avoidances = preferences.avoidances.filter(tag => (food.tags || []).indexOf(tag) != -1);
        }

        if (avoidances.length > 0) {
            className += " avoid";
            icon.push(
                <abbr className="avoid-warning" key="avoid-warning" title="This ingredient is on the avoidance list">
                    <i className="icon-warning3"/>
                </abbr>
            );
        }

        let description = [];

        if (grocery.description) {
            description = grocery.description.split(' ');

            // If there is a parenthesis in there, we inflect the first word
            if (grocery.description.indexOf('(') != -1) {
                description[0] = grocery.quantity == 1
                          ? singularize(description[0])
                          : pluralize(description[0]);
            } else {
                // Otherwise we inflect the last word
                description[description.length - 1] = grocery.quantity == 1
                          ? singularize(description[description.length - 1])
                          : pluralize(description[description.length - 1]);
            }
        }

        if ((food?.tags || []).indexOf('BUYORG') != -1 && !preferences.hide_ewg) {
            icon.push(
                <abbr className="buyorg" key={2} title="Buy Organic">
                    <i className="icon-ewg" />
                </abbr>
            );
        }

        if (grocery.exclude_from_groceries) {
            icon.push(
                <abbr key={3} title="Excluded from Groceries">
                    <i className="icon-minus-circle-solid" />
                </abbr>
            );
        }

        if (grocery.display_mode == 'prefix') {
            return (
                <p className={className}>
                    {description.join(' ')} <strong>{grocery.name}</strong>{icon}
                </p>
            );
        }

        if (grocery.display_mode == 'prefix-of') {
            return (
                <p className={className}>
                    {description.join(' ')} of <strong>{grocery.name}</strong>{icon}
                </p>
            );
        }

        if (grocery.display_mode == 'suffix') {
            return (
                <p className={className}>
                    <strong>{grocery.name}</strong>
                    {description.join(' ')}
                    {icon}
                </p>
            );
        }

        if (grocery.display_mode == 'dash-suffix') {
            return (
                <p className={className}>
                    <strong>{grocery.name}</strong> - {description.join(' ')}{icon}
                </p>
            );
        }

        if (grocery.name) {
            let split = grocery.name.split(' ');

            // Inflect the last word of the groceries name
            split[split.length - 1] = grocery.quantity == 1
                           ? singularize(split[split.length - 1])
                           : pluralize(split[split.length - 1]);

            return (
                <p className={className}>
                    <strong>{split.join(' ')}</strong>{icon}
                </p>
            );
        }

        return (
            <p className={className}>
                <strong>{grocery.uuid}</strong>{icon}
            </p>
        );
    };

    const getAmountAndUnitFromRecipeMeal = (meal, food) => {
        if (!recipes || !meal.recipe_uuid) {
            return { grams: 0, milliliters: 0, unit: null };
        }

        const recipe = recipes[meal.recipe_uuid];

        if (!recipe) {
            return { grams: 0, milliliters: 0, unit: null };
        }

        if (!details || !recipe.details) {
            return { grams: 0, milliliters: 0, unit: null };
        }

        const detail = details[recipe.details];

        if (!detail) {
            return { grams: 0, milliliters: 0, unit: null };
        }

        let grams = 0, milliliters = 0;
        let unit = null;

        flattenIngredients({detail}).forEach(ingredient => {
            if (food && ingredient.food && ingredient.food.uuid === food.uuid) {
                if (ingredient.grams) {
                    grams += (ingredient.grams) * (meal.scaling || 1);
                }

                if (ingredient.milliliters) {
                    milliliters += (ingredient.milliliters) * (meal.scaling || 1);
                }

                if (!unit && ingredient.food.unit_amount) {
                    unit = {
                        amount: ingredient.food.unit_amount,
                        description: ingredient.food.unit_description,
                        grams: ingredient.food.unit_grams,
                        milliliters: ingredient.food.unit_milliliters,
                    };
                }
            }

            if (ingredient.recipe && recipes && ingredient.recipe.uuid && recipes[ingredient.recipe.uuid]) {
                let subrecipe = recipes[ingredient.recipe.uuid];
                let subdetails = details && subrecipe.details && details[subrecipe.details];

                if (!(subrecipe && subdetails)) {
                    return;
                }

                let gramsNeeded = ingredient.grams * (meal.scaling || 1);
                let scaling = gramsNeeded / (subrecipe.grams_per_serving * (subrecipe.servings || 1));
                scaling = scaling > 0 ? Math.ceil(scaling) : Math.floor(scaling);

                flattenIngredients({subdetails}).forEach(subingredient => {
                    if (food && subingredient.food && subingredient.food.uuid === food.uuid) {
                        if (subingredient.grams) {
                            grams += (subingredient.grams * (1 / (1 - (food.refuse || 0) / 100))) * (scaling || 1);
                        }

                        if (subingredient.milliliters) {
                            milliliters += (subingredient.milliliters * (1 / (1 - (food.refuse || 0) / 100))) * (scaling || 1);
                        }

                        if (!unit && subingredient.food.unit_amount) {
                            unit = {
                                uuid: subingredient.food.uuid,
                                amount: subingredient.food.unit_amount,
                                description: subingredient.food.unit_description,
                                grams: subingredient.food.unit_grams,
                                milliliters: subingredient.food.milliliters,
                            };
                        }
                    }
                });
            }
        });

        if (!unit && food && food.default_unit) {
            unit = Object.assign({}, food.default_unit);
        }

        return { unit, grams, milliliters };
    };

    const getAmountAndUnitFromFoodMeal = (meal, food) => {
        if (!food) {
            return {
                unit: null,
                grams: 0,
                milliliters: 0,
                scaling: null,
            };
        }

        let unit = (food.units || []).find(fu => fu.description === meal.logged_unit) || food.default_unit;

        return {
            unit,
            grams: meal.logged_grams || 0,
            milliliters: meal.logged_milliliters || 0,
            scaling: meal.scaling,
        };
    };

    const renderAmountFromUnit = (unit, grams, milliliters, units_mode, scaling) => {
        let amount = (milliliters && (<span>{humanizeMilliliters(milliliters)}</span>)) ||
                     (grams && (<span>{humanizeGrams(grams)}</span>)) ||
                     '';

        if (unit && unit.grams > 0) {
            let t = smartCeil(grams / (unit.grams / unit.amount));

            let content = [
                units_mode === 'english' ? <SmartFraction value={t} key={1} /> : t,
            ];

            if (unit.description) {
                let desc = inflectUnitOfMeasure(t, unit);
                content.push(<span key={2}>&nbsp;{desc}</span>);
            }

            amount = (
                <span className="amount">{content}</span>
            );
        } else if (unit && unit.milliliters) {
            let t = smartCeil(milliliters / (unit.milliliters / unit.amount));

            let content = [
                units_mode === 'english' ? <SmartFraction value={t} key={1} /> : t,
            ];

            if (unit.description) {
                let desc = inflectUnitOfMeasure(t, unit);
                content.push(<span key={2}>{desc}</span>);
            }

            amount = (
                <span className="amount">{content}</span>
            );
        } else if (scaling) {
            amount = (
                <span className="amount">{scaling}</span>
            );
        }

        return amount;
    };

    const renderGroceryRecipeMeal = (meal, key, recipe, food) => {
        let { grams, milliliters, unit } = getAmountAndUnitFromRecipeMeal(meal, food);
        const amount = renderAmountFromUnit(unit, grams, milliliters, user.units_mode);

        // Use recipe_title from meal if recipe object is not available
        const recipeTitle = recipe?.title || meal.recipe_title || 'Unknown Recipe';

        // If we don't have amount data, just show the meal info
        if (!grams && !milliliters && !amount) {
            return (
                <li key={key} className="meal-usage-item">
                    <span className="meal-name">{recipeTitle}</span>
                    <span className="meal-day">on day {parseInt(meal.offset) + 1}</span>
                </li>
            );
        }

        return (
            <li key={key} className="meal-usage-item">
                <span className="meal-name">{recipeTitle}</span>
                <span className="meal-details">
                    on day {parseInt(meal.offset) + 1} needs <strong className="amount">{amount} ({milliliters ? <>{roundForHumans(milliliters)}ml</> : <>{roundForHumans(grams)}g</>})</strong>
                </span>
            </li>
        );
    };

    const renderGroceryFoodMeal = (meal, key, food) => {
        let { grams, milliliters, unit, scaling } = getAmountAndUnitFromFoodMeal(meal, food);
        const amount = renderAmountFromUnit(unit, grams, milliliters, user.units_mode, scaling);

        const mealName = meal.meal || 'Meal';

        // If we don't have amount data, just show the meal info
        if (!grams && !milliliters && !amount) {
            return (
                <li key={key} className="meal-usage-item">
                    <span className="meal-name">{mealName}</span>
                    <span className="meal-day">on day {parseInt(meal.offset) + 1}</span>
                </li>
            );
        }

        return (
            <li key={key} className="meal-usage-item">
                <span className="meal-name">{mealName}</span>
                <span className="meal-details">
                    on day {parseInt(meal.offset) + 1} needs <strong className="amount">{amount} ({milliliters ? <>{roundForHumans(milliliters)}ml</> : <>{roundForHumans(grams)}g</>})</strong>
                </span>
            </li>
        );
    };

    const renderGroceryMeal = (grocery, meal, key) => {
        const food = grocery.food_uuid && foods && foods[grocery.food_uuid];
        const recipe = meal.recipe_uuid && recipes && recipes[meal.recipe_uuid];

        // If we have a recipe_uuid, it's a recipe-based meal
        if (meal.recipe_uuid) {
            return renderGroceryRecipeMeal(meal, key, recipe, food);
        } 
        // Otherwise it's a direct food meal
        else {
            return renderGroceryFoodMeal(meal, key, food);
        }
    };

    const renderMeals = (grocery) => {
        if (!grocery.meals || !Array.isArray(grocery.meals)) {
            return null;
        }

        let groceryMeals = grocery.meals.sort((a, b) => a.offset - b.offset);

        if (!groceryMeals.length) {
            return null;
        }

        return (
            <section className="mapped-meals">
                <h4><em>{grocery.name}</em> is needed by:</h4>
                <ul>
                    {groceryMeals.map((meal, i) => {
                        const mealItem = renderGroceryMeal(grocery, meal, i);
                        return mealItem || null;
                    }).filter(Boolean)}
                </ul>
            </section>
        );
    };

    const renderGrocery = (grocery, key) => {
        let quantity = <i className="icon-warning" />;

        if (!isNaN(grocery.quantity) && typeof grocery.quantity == 'number') {
            quantity = (profile && profile.units_mode === 'metric')
                     ? roundForHumans(grocery.quantity)
                     : <SmartFraction value={grocery.quantity} />;
        }

        const grams = !isNaN(grocery.grams_needed) && typeof grocery.grams_needed == 'number'
                    ? Math.round(grocery.grams_needed)
                    : null;

        const milliliters = !isNaN(grocery.milliliters_needed) && typeof grocery.milliliters_needed == 'number'
                          ? Math.round(grocery.milliliters_needed)
                          : null;

        let pkg_used = <i className="icon-warning" />;
        let pkg_amt = <i className="icon-warning" />;

        if (!isNaN(grocery.milliliters_needed) && typeof grocery.milliliters_needed == 'number' &&
            !isNaN(grocery.quantity) && typeof grocery.quantity == 'number' && grocery.quantity > 0 &&
            !isNaN(grocery.pkg_milliliters) && typeof grocery.pkg_milliliters == 'number' && grocery.pkg_milliliters > 0) {
            pkg_used = Math.round(grocery.milliliters_needed / (grocery.pkg_milliliters * grocery.quantity) * 100);
            pkg_amt = Math.round(grocery.pkg_milliliters * grocery.quantity);
        } else if (!isNaN(grocery.grams_needed) && typeof grocery.grams_needed == 'number' &&
            !isNaN(grocery.quantity) && typeof grocery.quantity == 'number' && grocery.quantity > 0 &&
            !isNaN(grocery.pkg_grams) && typeof grocery.pkg_grams == 'number' && grocery.pkg_grams > 0) {
            pkg_used = Math.round(grocery.grams_needed / (grocery.pkg_grams * grocery.quantity) * 100);
            pkg_amt = Math.round(grocery.pkg_grams * grocery.quantity);
        }

        const excess = !isNaN(grocery.excess) && typeof grocery.excess == 'number'
                     ? (Math.round(grocery.excess * 100) / 100)
                     : <i className="icon-warning" />;

        const price = !isNaN(grocery.price) && typeof grocery.price == 'number'
                    ? '$' + grocery.price.toFixed(2)
                    : <i className="icon-warning" />;

        const used_price = !isNaN(grocery.used_price) && typeof grocery.price == 'number'
                    ? '$' + grocery.used_price.toFixed(2)
                    : <i className="icon-warning" />;

        return (
            <tr key={key}>
                <td className="img-cell">
                    <Popup positionClassName="el-popup-left-center" button={grocery.image ? <ImgResized src={grocery.image} width={32} height={32} /> : <i className="icon-logo" />}>
                        {renderMeals(grocery)}
                    </Popup>
                </td>
                <td className="value-cell">{quantity}</td>
                <td className="grocery-cell">
                    {user?.role === 'admin' && !isProSide()
                        ? <a target="_blank" href={`/admin/foods/${grocery.food_uuid}`}>
                            {renderByDisplayMode(grocery)}
                          </a>
                        : renderByDisplayMode(grocery)
                    }
                </td>
                <td>{grocery.category}</td>

                {showExtended && isAdminOrDnt ?
                    <>
                        <td className="value-cell">
                            {grams ? <span>{grams} <em>g</em></span> : null}
                            {milliliters ? <span>{milliliters} <em>ml</em></span> : null}
                            {!grams && !milliliters ?
                                <i className="icon-warning" />
                            : null}
                        </td>
                        <td className="value-cell">{pkg_amt} <em>{grocery.serving_unit}</em></td>
                        <td className="value-cell">{pkg_used} <em>%</em></td>
                        <td className="value-cell">{excess} <em>g</em></td>
                        <td className="value-cell">{grocery.refuse} <em>%</em></td>
                        <td className="value-cell">{used_price} <em></em></td>
                        <td className="value-cell">{price} <em></em></td>
                    </>
                : null}
            </tr>
        );
    };

    const getAggregates = (groceries) => {
        let price = 0, used_price = 0, grams = 0, excess = 0, purchased= 0;

        groceries.forEach(grocery => {
            if (!isNaN(grocery.price) && typeof grocery.price == 'number') {
                price += grocery.price;
            }

            if (!isNaN(grocery.grams_needed) && typeof grocery.grams_needed == 'number') {
                grams += grocery.grams_needed;
            }

            if (!isNaN(grocery.excess) && typeof grocery.excess == 'number') {
                excess += grocery.excess;
            }

            if (!isNaN(grocery.pkg_grams) && typeof grocery.pkg_grams == 'number' &&
                !isNaN(grocery.quantity) && typeof grocery.quantity == 'number') {
                purchased += grocery.quantity * grocery.pkg_grams;
            }

            if (!isNaN(grocery.used_price) && typeof grocery.used_price == 'number') {
                used_price += grocery.used_price;
            }
        });

        return {
            price: price.toFixed(2),
            grams: Math.round(grams),
            excess: Math.round(excess),
            used: Math.round(grams / purchased * 100),
            used_price: used_price.toFixed(2),
            purchased: Math.round(purchased),
        };
    };

    const renderTotals = (totals, title) => {
        return (
            <tr className="totals-row">
                <th></th>
                <th></th>
                <th></th>
                {showExtended && isAdminOrDnt ?
                    <>
                        <th className="value-cell"><strong>{title}</strong></th>
                        <th className="value-cell">{totals.grams} <em>g</em></th>
                        <th className="value-cell">{totals.purchased} <em>g</em></th>
                        <th className="value-cell">{totals.used} <em>%</em></th>
                        <th className="value-cell">{totals.excess} <em>g</em></th>
                        <th></th>
                        <th className="value-cell">${totals.used_price} <em></em></th>
                        <th className="value-cell">${totals.price} <em></em></th>
                    </>
                : null}
            </tr>
        );
    };

    // Get fresh and pantry groceries
    const freshGroceries = categorized?.fresh || [];
    const pantryGroceries = categorized?.pantry || [];
    
    // Calculate aggregates
    const freshAggregates = getAggregates(freshGroceries);
    const pantryAggregates = getAggregates(pantryGroceries);
    const overallAggregates = getAggregates(groceries);

    return (
        <Modal
            isOpen={true}
            onRequestClose={closeModal}
            className="el-modal el-modal4 customizer-groceries-modal"
            contentLabel="Meal Plan Groceries"
            overlayClassName="el-modal-overlay4"
            closeTimeoutMS={250}
        >
            <section className="el-modal-container el-modal4-container customizer-groceries-container">
                <header data-has-controls={false}>
                    <button className="el-modal-back-btn" onClick={closeModal}>
                        <span>Back</span>
                    </button>

                    <h2>Grocery List</h2>
                </header>

                <div className="el-modal-body-container el-modal4-body-container">
                    {isAdminOrDnt && (
                        <div className="grocery-toggle-section">
                            <div className="view-toggle">
                                <label htmlFor="grocery-detail-toggle">Show Details</label>
                                <button
                                    id="grocery-detail-toggle"
                                    className="el-toggle-switch"
                                    data-active={showExtended}
                                    onClick={handleToggleExtended}
                                >
                                    <span>Simple</span>
                                    <span>Detailed</span>
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="modal-scroll-container">
                        {totals.items === 0 ? (
                            <EmptyGroceryState />
                        ) : (
                            <table>
                                <tbody>
                                    <tr>
                                        <td colSpan={showExtended ? 11 : 4}><h3>Fresh Ingredients: {freshGroceries.length} total</h3></td>
                                    </tr>
                                    <tr>
                                        <th></th>
                                        <th className="value-cell">Qty</th>
                                        <th>Grocery</th>
                                        <th>Category</th>
                                        {showExtended && isAdminOrDnt ?
                                            <>
                                                <th className="value-cell">Needed</th>
                                                <th className="value-cell">To Purchase</th>
                                                <th className="value-cell">% Used</th>
                                                <th className="value-cell">Excess</th>
                                                <th className="value-cell">Refuse %</th>
                                                <th className="value-cell">Est.$/Used</th>
                                                <th className="value-cell">Est.$/Pkg</th>
                                            </>
                                        : null}
                                    </tr>
                                    {freshGroceries.map((grocery, i) => renderGrocery(grocery, i))}
                                    {showExtended && renderTotals(freshAggregates, 'Fresh Totals')}

                                    <tr>
                                        <td colSpan={showExtended ? 11 : 4}><h3>Pantry Ingredients: {pantryGroceries.length} total</h3></td>
                                    </tr>
                                    <tr>
                                        <th></th>
                                        <th className="value-cell">Qty</th>
                                        <th>Grocery</th>
                                        <th>Category</th>
                                        {showExtended && isAdminOrDnt ?
                                            <>
                                                <th className="value-cell">Needed</th>
                                                <th className="value-cell">To Purchase</th>
                                                <th className="value-cell">% Used</th>
                                                <th className="value-cell">Excess</th>
                                                <th className="value-cell">Refuse %</th>
                                                <th className="value-cell">Est.$/Used</th>
                                                <th className="value-cell">Est.$/Pkg</th>
                                            </>
                                        : null}
                                    </tr>
                                    {pantryGroceries.map((grocery, i) => renderGrocery(grocery, i))}
                                    {showExtended && renderTotals(pantryAggregates, 'Pantry Totals')}
                                    
                                    {showExtended && renderTotals(overallAggregates, 'Overall Totals')}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </section>
        </Modal>
    );
};

GroceriesModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    profile: PropTypes.object,
};

export default GroceriesModal;